import { createAction } from 'typesafe-actions';
import {
    ADD_COMPARISON_SELECTED_ITEM,
    GET_COMPARISON_SELECTED_FAILURE,
    GET_COMPARISON_SELECTED_REQUEST,
    GET_COMPARISON_SELECTED_SUCCESS,
    REMOVE_COMPARISON_SELECTED_ITEM,
    REMOVE_COMPARISON_VIEW_ITEM,
    SET_SELECTED_ITEMS,
    GET_SELECTED_LABELS_REQUEST,
    GET_SELECTED_LABELS_SUCCESS,
    GET_SELECTED_LABELS_FAILURE,
    INIT_COMPARISON_STATE,
} from './comparisonConstants';
import { Dispatch } from 'redux';
import {
    comparisonSelectedUuidsSelector,
    selectedLengthSelector,
} from './comparisonSelectors';
import { activeCurriculumTypeSelector } from '../AppConfig/appConfigSelectors';
import { client } from '../Common/gql/client';
import {
    comparisonQuery,
    pageQuery,
    selectedLabelsQuery,
} from './comparisonQuery';
import { Selected } from './comparisonReducer';
import { HydratedCurriculumType } from 'src/Common/types/AppConfigTypes';
import { activeCTSlugSelector } from '../List/listSelectors';
import { GetState } from '../Common/store/rootReducer';

type PageResult = { catalogContentRevisionUuid: string; path?: string };

export const initializeComparisonStateAction = createAction(
    INIT_COMPARISON_STATE,
)<HydratedCurriculumType[]>();

export const setSelectedItemsAction = createAction(SET_SELECTED_ITEMS)<{
    selectedUuids: string[];
    activeCTSlug: string;
}>();

export const setSelectedItems = (selectedUuids: string[]) => (
    dispatch: Dispatch,
    getState: GetState,
) => {
    const rootState = getState();
    const activeCTSlug = activeCTSlugSelector(rootState);

    return dispatch(setSelectedItemsAction({ selectedUuids, activeCTSlug }));
};

export const addComparisonSelectedItemAction = createAction(
    ADD_COMPARISON_SELECTED_ITEM,
)<{
    contentRevisionUuid: string;
    contentRevisionLabel: string;
    activeCTSlug: string;
}>();

export const addComparisonSelectedItem = (
    contentRevisionUuid: string,
    contentRevisionLabel: string,
) => (dispatch: Dispatch, getState: GetState) => {
    const rootState = getState();
    const numberOfSelected = selectedLengthSelector(rootState);

    if (numberOfSelected === 5) {
        return;
    }

    const activeCTSlug = activeCTSlugSelector(rootState);
    dispatch(
        addComparisonSelectedItemAction({
            contentRevisionUuid,
            contentRevisionLabel,
            activeCTSlug,
        }),
    );
};

export const removeComparisonSelectedItemAction = createAction(
    REMOVE_COMPARISON_SELECTED_ITEM,
)<{ contentRevisionUuid: string; activeCTSlug: string }>();

export const removeComparisonSelectedItem = (contentRevisionUuid: string) => (
    dispatch: Dispatch,
    getState: GetState,
) => {
    const rootState = getState();
    const numberOfSelected = selectedLengthSelector(rootState);

    if (numberOfSelected === 0) {
        return;
    }

    const activeCTSlug = activeCTSlugSelector(rootState);
    dispatch(
        removeComparisonSelectedItemAction({
            contentRevisionUuid,
            activeCTSlug,
        }),
    );
};

export const removeComparisonViewItemAction = createAction(
    REMOVE_COMPARISON_VIEW_ITEM,
)<string>();

export const removeComparisonViewItem = (contentRevisionUuid: string) => (
    dispatch: Dispatch,
    getState: GetState,
) => {
    const rootState = getState();
    const activeCTSlug = activeCTSlugSelector(rootState);

    dispatch(
        removeComparisonSelectedItemAction({
            contentRevisionUuid,
            activeCTSlug,
        }),
    );
    dispatch(removeComparisonViewItemAction(contentRevisionUuid));
};

export const getComparisonSelectedRequest = createAction(
    GET_COMPARISON_SELECTED_REQUEST,
)();
export const getComparisonSelectedSuccess = createAction(
    GET_COMPARISON_SELECTED_SUCCESS,
)<unknown[]>();
export const getComparisonSelectedFailure = createAction(
    GET_COMPARISON_SELECTED_FAILURE,
)<Error>();

export const getComparisonSelected = () => (
    dispatch: Dispatch,
    getState: GetState,
) => {
    dispatch(getComparisonSelectedRequest());
    const state = getState();
    const curriculumType = activeCurriculumTypeSelector(state);

    if (curriculumType === undefined) {
        dispatch(
            getComparisonSelectedFailure(
                new Error(
                    'Cannot get comparison selected without active curriculum type',
                ),
            ),
        );
        return;
    }

    const selectedUuids = comparisonSelectedUuidsSelector(state);
    const query = comparisonQuery({
        curriculumType,
        selectedUuids,
    });

    return client
        .query({
            query,
        })
        .then(async result => {
            const selectedValues = selectedUuids.map(
                (uuid: string, index: number) => {
                    return result.data[`selected${index}`];
                },
            );

            let pageValues: PageResult[] = [];
            try {
                const pageQueryResults = await client.query({
                    query: pageQuery({ selectedUuids }),
                });

                pageValues = selectedUuids.map(
                    (uuid: string, index: number) => {
                        return (
                            pageQueryResults &&
                            pageQueryResults.data[`page${index}`]
                        );
                    },
                );
            } catch (e) {
                console.error(e);
            } finally {
                const updatedSelected = selectedValues.map(
                    (selected: Selected) => {
                        return {
                            ...selected,
                            _pagePath: findMatchingPagePath(
                                selected.uuid,
                                pageValues,
                            ),
                        };
                    },
                );

                dispatch(getComparisonSelectedSuccess(updatedSelected));
            }
        })
        .catch(error => dispatch(getComparisonSelectedFailure(error)));
};

const findMatchingPagePath = (
    contentRevisionUuid: string,
    pageResults: PageResult[],
) => {
    const matchingPage = pageResults.find(
        pageData =>
            pageData &&
            pageData.catalogContentRevisionUuid === contentRevisionUuid,
    );

    return (matchingPage && matchingPage.path) || null;
};

export const getSelectedLabelsRequest = createAction(
    GET_SELECTED_LABELS_REQUEST,
)();

export const getSelectedLabelsSuccess = createAction(
    GET_SELECTED_LABELS_SUCCESS,
)<{ selected: Selected[]; activeCTSlug: string }>();

export const getSelectedLabelsFailure = createAction(
    GET_SELECTED_LABELS_FAILURE,
)<Error>();

export const getSelectedLabels = () => (
    dispatch: Dispatch,
    getState: GetState,
) => {
    dispatch(getSelectedLabelsRequest());
    const state = getState();
    const activeCTSlug = activeCTSlugSelector(state);
    const curriculumType = activeCurriculumTypeSelector(state);

    if (curriculumType === undefined) {
        dispatch(
            getSelectedLabelsFailure(
                new Error(
                    'Cannot get selected labels without active curriculum type',
                ),
            ),
        );
        return;
    }

    const selectedUuids = comparisonSelectedUuidsSelector(state);

    const query = selectedLabelsQuery({ curriculumType, selectedUuids });

    return client
        .query({ query })
        .then(result => {
            const selectedValues = selectedUuids.map(
                (uuid: string, index: number) => {
                    return result.data[`selected${index}`];
                },
            );

            const formatted = formatSelectedResults(
                curriculumType,
                selectedValues,
            );

            dispatch(
                getSelectedLabelsSuccess({
                    selected: formatted,
                    activeCTSlug,
                }),
            );
        })
        .catch(error => {
            console.error(error);
            dispatch(getSelectedLabelsFailure(error));
        });
};

const formatSelectedResults = (
    curriculumType: HydratedCurriculumType,
    selectedValues: { uuid: string; [nameKey: string]: string }[],
) => {
    const nameFieldSlug =
        curriculumType &&
        curriculumType.comparisonView &&
        curriculumType.comparisonView.name.field &&
        curriculumType.comparisonView.name.field.slug;

    return selectedValues.map(selectedValue => ({
        uuid: selectedValue.uuid,
        label: selectedValue[nameFieldSlug],
    }));
};
